import { CloseSharpIcon, KeyboardArrowDownIcon } from '@assets/icons';
import {
  Box,
  Chip,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select/SelectInput';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './MultiSelect.scss';

const AppMultiSelect: FC<AppMultiSelectProps> = ({
  id,
  required,
  label,
  isRow = false,
  options = [],
  placeholder,
  valueType = 'string',
  value,
  defaultValue,
  onChange,
  error,
}) => {
  const { t } = useTranslation();

  const [values, setValues] = useState<string[]>([]);

  const errorText = useMemo(() => {
    if (error?.type === 'required') {
      return t('common.please_select', { 0: t(label!) });
    }

    return error?.message ? t(error?.message) : null;
  }, [error]);

  useEffect(() => {
    if (defaultValue) {
      if (typeof defaultValue === 'string') {
        setValues(defaultValue.split(','));
      } else {
        setValues(defaultValue);
      }
    }
  }, []);

  useEffect(() => {
    if (value != null) {
      if (typeof value === 'string') {
        setValues(value ? value.split(',') : []);
      } else {
        setValues(value);
      }
    }
  }, [value]);

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value as string[];
    setValues(value);

    if (valueType === 'string') {
      event.target.value = value.join(',');
    }
    onChange?.(event);
  };

  const handleDelete = (id: string) => {
    const newValue = values.filter(ele => ele !== id);
    setValues(newValue);

    if (valueType === 'string') {
      onChange?.(newValue.join(','));
      return;
    }
    onChange?.(newValue);
  };

  const renderValue = (selected: string[]) => {
    if (!selected.length) {
      return <Typography>{t(placeholder!)}</Typography>;
    }
    return (
      <Box>
        {selected.map(value => (
          <Chip
            key={value}
            label={t(options.find(opt => opt.value === value)?.label ?? '')}
            color='primary'
            deleteIcon={<CloseSharpIcon />}
            onDelete={() => handleDelete(value)}
            onMouseDown={event => event.stopPropagation()}
          />
        ))}
      </Box>
    );
  };

  return (
    <Grid
      container
      className='MultiSelect'
      direction={isRow ? 'row' : 'column'}
      spacing={isRow ? 3 : 2}
    >
      {label && (
        <Grid
          item
          xs={4}
          alignItems='center'
          justifyContent={isRow ? 'flex-end' : 'flex-start'}
        >
          <FormLabel htmlFor={id} required={required}>
            {t(label)}
          </FormLabel>
        </Grid>
      )}
      <Grid item xs={8} sx={{ width: '100%' }}>
        <Select
          id={id}
          multiple
          displayEmpty
          value={values}
          IconComponent={KeyboardArrowDownIcon}
          renderValue={renderValue}
          onChange={handleChange}
          error={!!error?.type}
        >
          {options.map(({ value, label, disabled }) => (
            <MenuItem key={value} value={value} disabled={disabled}>
              {t(label)}
            </MenuItem>
          ))}
        </Select>
        {errorText && <FormHelperText error>{errorText}</FormHelperText>}
      </Grid>
    </Grid>
  );
};

export default AppMultiSelect;
